import { ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";

import { useAdminView } from "./AdminViewProvider";

/**
 * SPEC-23 — persistent banner shown while the admin is viewing or
 * editing another user's data. Tied to the warning palette so the
 * "you're touching foreign data" risk is visually unmistakable.
 *
 * SPEC-24 — solid amber bar so the banner remains legible in both
 * themes. The previous `bg-warning/15` made the background nearly
 * invisible in dark mode, so the near-black warning-foreground text
 * sat on top of the page background; switching to a solid `--warning`
 * fill puts the dark text back on amber where it was designed to
 * read.
 *
 * Click "Çık" → clears the context and navigates back to `/admin`.
 */
export function AdminViewBanner() {
  const { adminView, ownerEmail, clear } = useAdminView();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!adminView) return null;

  const handleExit = (): void => {
    clear();
    navigate("/admin");
  };

  return (
    <div
      role="alert"
      data-testid="admin-view-banner"
      className="flex flex-wrap items-center justify-between gap-2 border-b border-warning px-4 py-2.5 text-sm font-medium text-[hsl(var(--warning-foreground))]"
      style={{ background: "hsl(var(--warning))" }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <ShieldAlert className="h-4 w-4 shrink-0 text-[hsl(var(--warning-foreground))]" />
        <span className="truncate">
          {t("admin.viewBanner.text", {
            email: ownerEmail ?? t("admin.viewBanner.unknownEmail"),
          })}
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleExit}
        data-testid="admin-view-banner-exit"
        className="border-[hsl(var(--warning-foreground))]/40 bg-transparent text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning-foreground))]/10 hover:text-[hsl(var(--warning-foreground))]"
      >
        {t("admin.viewBanner.exit")}
      </Button>
    </div>
  );
}
